import { useCallback, useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cx } from '../../lib/cx';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  title: ReactNode;
  /** e.g. "Step 1 of 2" — shown next to the title in the header. */
  stepLabel?: string;
  /** Step 2 of the key modals has no close control at all (see the "Create a new client" section). */
  onClose?: (() => void) | undefined;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
}

const WIDTHS = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-2xl' } as const;

export function Modal({
  open,
  title,
  stepLabel,
  onClose,
  children,
  footer,
  width = 'md',
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  const trapFocus = useCallback((event: KeyboardEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    // `disabled` controls are already excluded by the selector; anything else inside an
    // open dialog is reachable, so no further visibility filtering is applied here.
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  // Kept in a ref so the focus effect below depends only on `open` — otherwise a fresh
  // `onClose` closure on every render would re-run it and yank focus back mid-typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    restoreFocusTo.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    /*
     * Open on the first thing the reader came here to use — the first field of a form, or
     * Cancel in a confirmation — not on the close control.
     *
     * The `×` is first in DOM order because it belongs in the header, so a plain "focus
     * the first focusable" landed there every time: every dialog in the console opened
     * with the keyboard sitting on Close, and reaching the Email field of "Add Admin User"
     * meant tabbing past it. Falling back to the panel (rather than to the `×`) keeps the
     * dialog's title as the first thing announced.
     */
    const focusable = Array.from(panel?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const firstField = focusable.find(
      (element) =>
        !element.hasAttribute('data-modal-dismiss') &&
        // Links are skipped for the *opening* position only — they stay in the tab order
        // and in the trap. A link leads out of the dialog, so it is never what the reader
        // opened it to do, and the one in the client detail view sits at the very bottom:
        // landing there scrolled the dialog past everything it had come to show.
        element.tagName !== 'A',
    );
    (firstField ?? panel)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      const close = onCloseRef.current;
      if (event.key === 'Escape' && close) {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key === 'Tab') trapFocus(event);
    };

    document.addEventListener('keydown', onKeyDown, true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      restoreFocusTo.current?.focus?.();
    };
  }, [open, trapFocus]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-hidden p-4 sm:p-10">
      {/* Dimmed backdrop: the page underneath stays visible, per the spec's modal convention. */}
      <div
        className="fixed inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose ? () => onClose() : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx(
          // A dialog sits above everything else and should read that way: the same
          // elevation the popovers use, not the 1px card shadow.
          'relative w-full rounded border border-hairline-strong bg-surface-raised shadow-popover',
          // Never taller than the space it is given: the body scrolls inside, and the
          // header and footer stay put. The whole panel used to scroll within the
          // backdrop instead, so New Client opened on a 720px screen with "Create
          // client →" already below the fold — a form whose submit button you have to go
          // looking for reads as a form that is still loading.
          'flex max-h-full flex-col',
          WIDTHS[width],
        )}
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-hairline px-4 py-3">
          <h2 id={titleId} className="flex-1 text-[14px] font-semibold text-ink">
            {title}
          </h2>
          {stepLabel ? (
            <span className="text-2xs font-medium uppercase tracking-wide text-ink-subtle">
              {stepLabel}
            </span>
          ) : null}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-modal-dismiss=""
              className="rounded p-1 text-ink-subtle hover:bg-surface-sunken hover:text-ink"
            >
              <X size={15} aria-hidden="true" />
            </button>
          ) : null}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-hairline px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
